import React, { useState, useEffect, useCallback } from "react";
import { getManualAssets, createManualAsset, updateManualAsset, deleteManualAsset, getNetWorthSummary, getMetalRates, refreshMetalPrices, sgbLookup, uploadAssetPhoto, deleteAssetPhoto, assetPhotoUrl, getLiabilities, createLiability, deleteLiability } from "../lib/api";
import { Plus, Trash2, Edit2, Loader2, Landmark, Gem, Building2, PiggyBank, Banknote, CarFront, Save, X, ChevronDown, ChevronRight, RefreshCw, Zap, Camera, Image as ImageIcon, Upload, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const ASSET_TABS = [
  { key: "debt", label: "Debt & Retirement", icon: Landmark, categories: ["SSY", "PPF", "EPF", "NPS", "Debt Fund", "Other Debt"] },
  { key: "alternative", label: "Alternatives", icon: Gem, categories: ["Gold", "Silver", "SGB", "Private Equity", "Unlisted Shares", "Other"] },
  { key: "real_estate", label: "Real Estate", icon: Building2, categories: ["Property"] },
  { key: "bank_savings", label: "Bank Savings", icon: PiggyBank, categories: ["Savings Account"] },
  { key: "fd", label: "Fixed Deposits", icon: Banknote, categories: ["FD"] },
  { key: "vehicle", label: "Vehicles", icon: CarFront, categories: ["Vehicle"] },
];

const FREQ_OPTIONS = ["Manual", "Monthly", "Quarterly", "Annual"];

export default function AssetsPage({ initialTab = "real_estate", section = "assets" }) {
  const [tab, setTab] = useState(initialTab);
  const [assets, setAssets] = useState([]);
  const [totals, setTotals] = useState({});
  const [grandTotal, setGrandTotal] = useState(0);
  const [nw, setNw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState(emptyForm("debt"));
  const [metalRates, setMetalRates] = useState(null);
  const [refreshingMetals, setRefreshingMetals] = useState(false);

  const load = useCallback(async () => {
    try {
      const [aRes, nwRes] = await Promise.all([getManualAssets(), getNetWorthSummary()]);
      setAssets(aRes.data.assets || []);
      setTotals(aRes.data.totals || {});
      setGrandTotal(aRes.data.grand_total || 0);
      setNw(nwRes.data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Load metal rates when dialog opens on Gold/Silver
  useEffect(() => {
    if (open && (form.category === "Gold" || form.category === "Silver") && !metalRates) {
      getMetalRates().then(r => setMetalRates(r.data)).catch(() => {});
    }
  }, [open, form.category, metalRates]);

  const handleRefreshMetals = async () => {
    setRefreshingMetals(true);
    try {
      const { data } = await refreshMetalPrices();
      setMetalRates(data.rates);
      await load();
    } catch {}
    setRefreshingMetals(false);
  };

  const currentTab = ASSET_TABS.find(t => t.key === tab);
  const filtered = assets.filter(a => a.asset_type === tab);
  const tabTotal = totals[tab]?.current_value || 0;
  const tabInvested = totals[tab]?.invested_value || 0;

  function emptyForm(type) {
    const t = ASSET_TABS.find(x => x.key === type) || ASSET_TABS[0];
    return { category: t.categories[0], asset_type: type, name: "", holder: "", owner_id: "unassigned", ownership_pct: "100", currency: "INR", current_value: "", invested_value: "", quantity: "", num_units: "1", price_per_unit: "", purity: "", auto_price: false, has_diamond: false, diamond_value: "", sgb_cmp: "", account_number: "", bank_name: "", update_frequency: "Manual", valuation_date: "", notes: "" };
  }

  const handleSubmit = async () => {
    if (!form.name) return;
    const isMetal = form.category === "Gold" || form.category === "Silver";
    const isSgb = form.category === "SGB";
    // For metals: compute total grams = num_units × weight_per_unit
    const numUnits = parseFloat(form.num_units) || 1;
    const weightPerUnit = parseFloat(form.quantity) || 0;
    const totalGrams = isMetal ? numUnits * weightPerUnit : weightPerUnit;

    // SGB invested/current calc
    let sgbInvested = null, sgbCurrent = null;
    if (isSgb) {
      const q = parseFloat(form.quantity) || 0;
      const p = parseFloat(form.price_per_unit) || 0;
      const cmp = parseFloat(form.sgb_cmp) || 0;
      if (q <= 0 || p <= 0) return;
      sgbInvested = q * p;
      sgbCurrent = cmp > 0 ? q * cmp : sgbInvested;
    }

    if (!isMetal && !isSgb && !form.current_value) return;
    if (isMetal && !form.auto_price && !form.current_value) return;
    if (isMetal && form.auto_price && totalGrams <= 0) return;

    const payload = {
      ...form,
      ownership_pct: parseFloat(form.ownership_pct) || 100,
      current_value: isSgb ? sgbCurrent : (parseFloat(form.current_value) || 0),
      invested_value: isSgb ? sgbInvested : (form.invested_value ? parseFloat(form.invested_value) : null),
      quantity: isMetal ? totalGrams : (form.quantity ? parseFloat(form.quantity) : null),
      price_per_unit: form.price_per_unit ? parseFloat(form.price_per_unit) : null,
      purity: form.purity || null,
      auto_price: !!form.auto_price,
      has_diamond: !!form.has_diamond,
      diamond_value: form.has_diamond ? (parseFloat(form.diamond_value) || 0) : 0,
      bank_name: isSgb ? (form.bank_name || "") : form.bank_name,
      notes: isMetal && numUnits > 1
        ? `${form.notes || ""}${form.notes ? " · " : ""}${numUnits} units × ${weightPerUnit}g each`.trim()
        : (isSgb && form.sgb_cmp ? `${form.notes || ""}${form.notes ? " · " : ""}CMP: ₹${form.sgb_cmp}`.trim() : form.notes),
    };
    try {
      if (editId) { await updateManualAsset(editId, payload); }
      else { await createManualAsset(payload); }
      setOpen(false); setEditId(null); setForm(emptyForm(tab)); load();
    } catch {}
  };

  const handleEdit = (a) => {
    setEditId(a.id);
    setForm({
      category: a.category, asset_type: a.asset_type, name: a.name, holder: a.holder || "",
      owner_id: a.owner_id || "unassigned",
      ownership_pct: String(a.ownership_pct ?? 100),
      currency: a.currency || "INR",
      current_value: String(a.current_value || ""),
      invested_value: a.invested_value ? String(a.invested_value) : "",
      quantity: a.quantity ? String(a.quantity) : "",
      num_units: "1",
      price_per_unit: a.price_per_unit ? String(a.price_per_unit) : "",
      purity: a.purity || "",
      auto_price: !!a.auto_price,
      has_diamond: !!a.has_diamond,
      diamond_value: a.diamond_value ? String(a.diamond_value) : "",
      account_number: a.account_number || "",
      bank_name: a.bank_name || "",
      update_frequency: a.update_frequency || "Manual",
      valuation_date: a.valuation_date || "",
      notes: a.notes || "",
    });
    setOpen(true);
  };

  const handleDelete = async (id) => { await deleteManualAsset(id); load(); };

  // Group by category
  const grouped = {};
  for (const a of filtered) { const c = a.category || "Other"; if (!grouped[c]) grouped[c] = []; grouped[c].push(a); }

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[var(--brand)]" size={28} /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-5" data-testid="assets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>{section === "debt-retirement" ? "Debt & Retirement" : "Property & Other Assets"}</h1>
          <p className="text-sm text-[var(--text-muted)]">{section === "debt-retirement" ? "PPF, EPF, NPS, SSY and other debt holdings" : "Real estate, alternatives, deposits, savings and vehicles"}</p>
        </div>
        <div className="flex items-center gap-2">
          <LiabilityManager onChanged={load} />
          {tab === "alternative" && (
            <Button onClick={handleRefreshMetals} disabled={refreshingMetals} className="bg-amber-500/10 border border-amber-500/25 text-amber-400 hover:bg-amber-500/20 text-xs gap-1.5" data-testid="refresh-metals-btn">
              {refreshingMetals ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              Refresh Gold/Silver
            </Button>
          )}
          <Dialog open={open} onOpenChange={(v) => {
            setOpen(v);
            if (!v) { setEditId(null); setForm(emptyForm(tab)); }
            else if (!editId) { setForm(emptyForm(tab)); }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white text-xs gap-1.5" data-testid="add-asset-btn"><Plus size={14} /> Add Asset</Button>
            </DialogTrigger>
          <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "New"} Asset</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-2">
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.asset_type} onValueChange={v => setForm(f => ({ ...f, asset_type: v, category: ASSET_TABS.find(t => t.key === v)?.categories[0] || "" }))}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>{ASSET_TABS.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue placeholder="Category" /></SelectTrigger>
                  <SelectContent>{(ASSET_TABS.find(t => t.key === form.asset_type)?.categories || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Input placeholder="Asset / Account Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-name" />
              <Input placeholder="Account Holder / Owner" value={form.holder} onChange={e => setForm(f => ({ ...f, holder: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
              <div className="grid grid-cols-3 gap-3">
                <Select value={form.owner_id} onValueChange={v => setForm(f => ({ ...f, owner_id: v }))}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue placeholder="Owner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aman">Aman</SelectItem>
                    <SelectItem value="gurpreet">Gurpreet</SelectItem>
                    <SelectItem value="joint">Joint</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                  </SelectContent>
                </Select>
                <Input placeholder="Ownership %" type="number" min="0" max="100" value={form.ownership_pct} onChange={e => setForm(f => ({ ...f, ownership_pct: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue placeholder="Currency" /></SelectTrigger>
                  <SelectContent><SelectItem value="INR">INR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                </Select>
              </div>

              {/* Gold / Silver special flow */}
              {(form.category === "Gold" || form.category === "Silver") ? (
                <GoldSilverBlock form={form} setForm={setForm} metalRates={metalRates} />
              ) : form.category === "SGB" ? (
                <SgbBlock form={form} setForm={setForm} />
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <Input placeholder="Current Value *" type="number" value={form.current_value} onChange={e => setForm(f => ({ ...f, current_value: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-value" />
                    <Input placeholder="Invested Value" type="number" value={form.invested_value} onChange={e => setForm(f => ({ ...f, invested_value: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                  </div>
                  {(form.category === "Private Equity" || form.category === "Unlisted Shares") && (
                    <div className="grid grid-cols-2 gap-3">
                      <Input placeholder="Quantity / Units" type="number" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                      <Input placeholder="Price per Unit" type="number" value={form.price_per_unit} onChange={e => setForm(f => ({ ...f, price_per_unit: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                    </div>
                  )}
                </>
              )}
              {(form.asset_type === "bank_savings" || form.asset_type === "fd") && (
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Bank Name" value={form.bank_name} onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                  <Input placeholder="Account No." value={form.account_number} onChange={e => setForm(f => ({ ...f, account_number: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Select value={form.update_frequency} onValueChange={v => setForm(f => ({ ...f, update_frequency: v }))}>
                  <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>{FREQ_OPTIONS.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                </Select>
                <Input type="date" placeholder="Valuation Date" value={form.valuation_date} onChange={e => setForm(f => ({ ...f, valuation_date: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
              </div>
              <textarea placeholder="Notes..." value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full bg-[var(--bg-base)] border border-white/10 rounded-md p-2.5 text-sm text-white placeholder:text-[var(--text-muted)] min-h-[60px] resize-none" />
              <Button onClick={handleSubmit} disabled={!form.name} className="w-full bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white" data-testid="asset-submit"><Save size={14} className="mr-1" /> {editId ? "Update" : "Save"} Asset</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Net Worth Summary Strip */}
      {nw && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-md p-4" data-testid="nw-summary">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Total Net Worth</div>
              <div className="font-mono text-xl text-white">{fmtV(nw.grand_total)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Market Portfolio</div>
              <div className="font-mono text-lg text-[var(--text-secondary)]">{fmtV(nw.portfolio_value)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Other Assets</div>
              <div className="font-mono text-lg text-[var(--text-secondary)]">{fmtV(nw.manual_assets_value)}</div>
            </div>
            <div>
              <div className="text-[9px] uppercase text-[var(--text-muted)] tracking-wider">Breakdown</div>
              <div className="space-y-0.5 mt-1">
                {Object.entries(nw.breakdown || {}).map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[10px]">
                    <span className="text-[var(--text-muted)]">{k}</span>
                    <span className="font-mono text-white">{fmtV(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[var(--bg-surface)] border border-white/10 rounded-md p-0.5 w-fit" data-testid="asset-tabs">
        {ASSET_TABS.map(t => {
          const Icon = t.icon;
          const count = (totals[t.key]?.count || 0);
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition-colors ${tab === t.key ? "bg-[var(--brand)] text-white" : "text-[var(--text-secondary)] hover:text-white"}`}
              data-testid={`tab-${t.key}`}>
              <Icon size={13} />
              {t.label}
              {count > 0 && <span className="text-[9px] bg-white/10 px-1 rounded">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Tab Summary */}
      {tabTotal > 0 && (
        <div className="flex gap-4 text-sm">
          <span className="text-[var(--text-muted)]">Total: <span className="font-mono text-white">{fmtV(tabTotal)}</span></span>
          {tabInvested > 0 && (
            <>
              <span className="text-[var(--text-muted)]">Invested: <span className="font-mono text-[var(--text-secondary)]">{fmtV(tabInvested)}</span></span>
              <span className={`font-mono ${tabTotal >= tabInvested ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                {tabTotal >= tabInvested ? "+" : ""}{fmtV(tabTotal - tabInvested)} ({((tabTotal - tabInvested) / tabInvested * 100).toFixed(1)}%)
              </span>
            </>
          )}
        </div>
      )}

      {/* Assets List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-[var(--bg-surface)] border border-white/10 rounded-md">
          {currentTab && <currentTab.icon size={36} className="mx-auto text-[var(--text-muted)] mb-3 opacity-30" />}
          <p className="text-sm text-[var(--text-muted)]">No {currentTab?.label} assets yet. Click "Add Asset" to start.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {Object.entries(grouped).map(([cat, items]) => {
            const isOpen = expanded[cat] !== false; // default open
            const catTotal = items.reduce((s, a) => s + (a.current_value_inr ?? a.current_value ?? 0), 0);
            return (
              <div key={cat} className="bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-white/[0.02]" onClick={() => setExpanded(e => ({ ...e, [cat]: !isOpen }))}>
                  <div className="flex items-center gap-2">
                    {isOpen ? <ChevronDown size={14} className="text-[var(--text-muted)]" /> : <ChevronRight size={14} className="text-[var(--text-muted)]" />}
                    <span className="text-sm font-medium text-white">{cat}</span>
                    <span className="text-[10px] text-[var(--text-muted)] bg-white/5 px-1.5 py-0.5 rounded">{items.length}</span>
                  </div>
                  <span className="font-mono text-sm text-white">{fmtV(catTotal)}</span>
                </div>
                {isOpen && (
                  <div className="border-t border-white/5">
                    {items.map(a => (
                      <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-white/[0.03] last:border-0 hover:bg-white/[0.02]" data-testid={`asset-${a.id}`}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          {(a.category === "Gold" || a.category === "Silver") && (
                            <AssetPhotoCell asset={a} onChanged={load} />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-white">{a.name}</span>
                              {a.holder && <span className="text-[10px] text-[var(--text-muted)] bg-white/5 px-1.5 py-0.5 rounded">{a.holder}</span>}
                            </div>
                            <div className="flex gap-3 mt-1 text-[10px] text-[var(--text-muted)]">
                              {a.bank_name && <span>{a.bank_name}</span>}
                              {a.account_number && <span>A/C: {a.account_number}</span>}
                              {a.quantity && (a.category === "Gold" || a.category === "Silver") ? (
                                <span className="font-mono">{a.quantity}g {a.purity && `· ${a.purity}`}{a.live_rate_per_gram ? ` · live ₹${a.live_rate_per_gram.toLocaleString("en-IN")}/g` : ""}{a.has_diamond && a.diamond_value ? ` · 💎 ₹${a.diamond_value.toLocaleString("en-IN")}` : ""}</span>
                              ) : a.quantity && (
                                <span>{a.quantity} units</span>
                              )}
                              {a.price_per_unit && (a.category !== "Gold" && a.category !== "Silver") && <span>@ {a.price_per_unit.toLocaleString()}/unit</span>}
                              {a.auto_price && <span className="text-amber-400">⚡ auto</span>}
                              <span>{a.update_frequency}</span>
                              {a.valuation_date && <span>As of {a.valuation_date}</span>}
                            </div>
                            {a.notes && <div className="text-[10px] text-[var(--text-muted)] mt-0.5 italic">{a.notes}</div>}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            <div className="font-mono text-sm text-white">{fmtV(a.current_value_inr ?? a.current_value)} <span className="text-[9px] text-[var(--text-muted)]">INR</span></div>
                            {a.currency === "USD" && <div className="font-mono text-[9px] text-[var(--text-muted)]">${Number(a.current_value || 0).toLocaleString("en-US")}</div>}
                            {(a.invested_value_inr ?? a.invested_value ?? 0) > 0 && (
                              <div className={`font-mono text-[10px] ${(a.current_value_inr ?? a.current_value ?? 0) >= (a.invested_value_inr ?? a.invested_value ?? 0) ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>
                                {(a.current_value_inr ?? a.current_value ?? 0) >= (a.invested_value_inr ?? a.invested_value ?? 0) ? "+" : ""}{fmtV((a.current_value_inr ?? a.current_value ?? 0) - (a.invested_value_inr ?? a.invested_value ?? 0))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => handleEdit(a)} className="text-[var(--text-muted)] hover:text-white p-1"><Edit2 size={13} /></button>
                            <button onClick={() => handleDelete(a.id)} className="text-[var(--text-muted)] hover:text-red-400 p-1"><Trash2 size={13} /></button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const EMPTY_LIABILITY = {
  liability_type: "home_loan", name: "", lender: "", owner_id: "unassigned",
  outstanding_balance: "", interest_rate_pct: "", emi: "", remaining_months: "",
  currency: "INR", valuation_date: "", notes: "",
};

function LiabilityManager({ onChanged }) {
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState([]);
  const [form, setForm] = React.useState(EMPTY_LIABILITY);
  const [saving, setSaving] = React.useState(false);

  const loadRows = React.useCallback(async () => {
    try { const { data } = await getLiabilities(); setRows(data.liabilities || []); }
    catch { setRows([]); }
  }, []);

  React.useEffect(() => { if (open) loadRows(); }, [open, loadRows]);

  const save = async () => {
    if (!form.name || !form.outstanding_balance) return;
    setSaving(true);
    try {
      await createLiability({
        ...form,
        outstanding_balance: Number(form.outstanding_balance),
        interest_rate_pct: form.interest_rate_pct ? Number(form.interest_rate_pct) : null,
        emi: form.emi ? Number(form.emi) : null,
        remaining_months: form.remaining_months ? Number(form.remaining_months) : null,
      });
      setForm(EMPTY_LIABILITY);
      await loadRows();
      await onChanged?.();
      toast.success("Liability added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save liability");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await deleteLiability(id); await loadRows(); await onChanged?.(); }
    catch { toast.error("Could not delete liability"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="border-red-500/25 text-red-300 hover:bg-red-500/10 text-xs gap-1.5" data-testid="manage-liabilities-btn">
          <CreditCard size={14} /> Liabilities
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--bg-surface)] border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white">Household Liabilities</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <Input placeholder="Loan / liability name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Input placeholder="Lender" value={form.lender} onChange={e => setForm(f => ({ ...f, lender: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Select value={form.liability_type} onValueChange={v => setForm(f => ({ ...f, liability_type: v }))}>
            <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="home_loan">Home loan</SelectItem><SelectItem value="vehicle_loan">Vehicle loan</SelectItem>
              <SelectItem value="personal_loan">Personal loan</SelectItem><SelectItem value="credit_card">Credit card</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.owner_id} onValueChange={v => setForm(f => ({ ...f, owner_id: v }))}>
            <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="aman">Aman</SelectItem><SelectItem value="gurpreet">Gurpreet</SelectItem><SelectItem value="joint">Joint</SelectItem><SelectItem value="unassigned">Unassigned</SelectItem></SelectContent>
          </Select>
          <Input type="number" min="0" placeholder="Outstanding balance *" value={form.outstanding_balance} onChange={e => setForm(f => ({ ...f, outstanding_balance: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Input type="number" min="0" placeholder="Interest rate %" value={form.interest_rate_pct} onChange={e => setForm(f => ({ ...f, interest_rate_pct: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Input type="number" min="0" placeholder="Monthly EMI" value={form.emi} onChange={e => setForm(f => ({ ...f, emi: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Input type="number" min="0" placeholder="Remaining months" value={form.remaining_months} onChange={e => setForm(f => ({ ...f, remaining_months: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
          <Select value={form.currency} onValueChange={v => setForm(f => ({ ...f, currency: v }))}>
            <SelectTrigger className="bg-[var(--bg-base)] border-white/10 text-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="INR">INR</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
          </Select>
          <Input type="date" value={form.valuation_date} onChange={e => setForm(f => ({ ...f, valuation_date: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" />
        </div>
        <textarea placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-3 bg-[var(--bg-base)] border border-white/10 rounded-md p-2.5 text-sm text-white min-h-[56px]" />
        <Button onClick={save} disabled={saving || !form.name || !form.outstanding_balance} className="w-full mt-3 bg-red-500/80 hover:bg-red-500 text-white">
          {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />} Add Liability
        </Button>
        <div className="space-y-2 mt-4">
          {rows.map(row => (
            <div key={row.id} className="flex items-center justify-between bg-[var(--bg-base)] border border-white/5 rounded-md p-3">
              <div><div className="text-sm text-white">{row.name}</div><div className="text-[10px] text-[var(--text-muted)]">{row.lender || row.liability_type} · {row.owner_id} · as of {row.valuation_date}</div></div>
              <div className="flex items-center gap-3"><span className="font-mono text-red-300">{row.currency === "USD" ? "$" : "₹"}{Number(row.outstanding_balance || 0).toLocaleString("en-IN")}</span><button onClick={() => remove(row.id)} className="text-[var(--text-muted)] hover:text-red-400"><Trash2 size={13} /></button></div>
            </div>
          ))}
          {!rows.length && <div className="text-xs text-[var(--text-muted)] text-center py-3">No liabilities recorded.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SgbBlock({ form, setForm }) {
  const [lookingUp, setLookingUp] = React.useState(false);
  const [lookupMsg, setLookupMsg] = React.useState("");
  const qty = parseFloat(form.quantity) || 0;
  const pricePerUnit = parseFloat(form.price_per_unit) || 0;
  const cmp = parseFloat(form.sgb_cmp) || 0;
  const totalInvested = qty * pricePerUnit;
  const totalCurrent = cmp > 0 ? qty * cmp : totalInvested;
  const pnl = totalCurrent - totalInvested;
  const pnlPct = totalInvested ? (pnl / totalInvested) * 100 : 0;

  const fetchCmp = async () => {
    if (!form.name) { setLookupMsg("Enter SGB name/symbol first"); return; }
    setLookingUp(true); setLookupMsg("");
    try {
      const { data } = await sgbLookup(form.name);
      if (data.price) {
        setForm(f => ({ ...f, sgb_cmp: String(data.price) }));
        const prefix = data.source === "yfinance" ? "✓ Live NSE price" : "✓ Gold-NAV estimate";
        setLookupMsg(`${prefix} · ₹${data.price}/unit · ${data.note || ""}`);
      } else {
        setLookupMsg("Could not fetch. Enter CMP manually below.");
      }
    } catch {
      setLookupMsg("Lookup failed. Enter CMP manually.");
    }
    setLookingUp(false);
  };

  return (
    <div className="space-y-3">
      <div className="bg-amber-500/5 border border-amber-500/15 rounded-md p-2.5">
        <p className="text-[11px] text-[var(--text-secondary)]">
          <span className="text-amber-400 font-semibold">SGB (Sovereign Gold Bond)</span> — enter the bond series name
          and we'll try to fetch the current market price from NSE.
        </p>
      </div>

      {/* SGB Name + CMP lookup */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">SGB Series / Symbol *</div>
          <button
            type="button"
            onClick={fetchCmp}
            disabled={lookingUp || !form.name}
            className="text-[10px] text-cyan-400 hover:text-cyan-300 disabled:opacity-40 flex items-center gap-1"
            data-testid="sgb-fetch-cmp"
          >
            {lookingUp ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Fetch live price
          </button>
        </div>
        <Input
          placeholder="e.g. SGBOCT28IV or SGBJUN32"
          value={form.name}
          onChange={e => setForm(f => ({ ...f, name: e.target.value.toUpperCase() }))}
          className="bg-[var(--bg-base)] border-white/10 text-white font-mono"
          data-testid="sgb-name"
        />
        {lookupMsg && <p className={`text-[10px] mt-1 ${lookupMsg.startsWith("✓") ? "text-emerald-400" : "text-[var(--text-muted)]"}`} data-testid="sgb-lookup-msg">{lookupMsg}</p>}
      </div>

      {/* Quantity + Price per unit + CMP */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Quantity *</div>
          <Input type="number" placeholder="e.g. 10" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="sgb-qty" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Price / unit (₹) *</div>
          <Input type="number" placeholder="Issue price" value={form.price_per_unit} onChange={e => setForm(f => ({ ...f, price_per_unit: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="sgb-price" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">CMP (₹)</div>
          <Input type="number" placeholder="Auto or enter" value={form.sgb_cmp} onChange={e => setForm(f => ({ ...f, sgb_cmp: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="sgb-cmp" />
        </div>
      </div>

      {/* Account */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Held in Account / Broker</div>
        <Input
          placeholder="e.g. Angel Broking, Zerodha"
          value={form.bank_name}
          onChange={e => setForm(f => ({ ...f, bank_name: e.target.value }))}
          className="bg-[var(--bg-base)] border-white/10 text-white"
          data-testid="sgb-broker"
        />
      </div>

      {/* Preview */}
      {qty > 0 && pricePerUnit > 0 && (
        <div className="bg-[var(--bg-base)] border border-white/5 rounded-md p-3 space-y-1.5" data-testid="sgb-preview">
          <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Total Invested</span><span className="font-mono text-[var(--text-secondary)]">₹{totalInvested.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Current {cmp > 0 ? "" : "(= invested, enter CMP to refine)"}</span><span className="font-mono text-white">₹{totalCurrent.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          {cmp > 0 && totalInvested > 0 && (
            <div className="flex justify-between text-xs pt-1 border-t border-white/5"><span className="text-[var(--text-muted)]">P&L</span><span className={`font-mono font-semibold ${pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{pnl >= 0 ? "+" : ""}₹{pnl.toLocaleString("en-IN", { maximumFractionDigits: 0 })} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)</span></div>
          )}
        </div>
      )}
    </div>
  );
}


function fmtV(v) {
  if (!v && v !== 0) return "-";
  const abs = Math.abs(v);
  if (abs >= 1e7) return `${(v / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${(v / 1e5).toFixed(2)}L`;
  if (abs >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function GoldSilverBlock({ form, setForm, metalRates }) {
  const isGold = form.category === "Gold";
  const purities = isGold
    ? [
        { v: "24K", label: "24K (Pure / 999)" },
        { v: "22K", label: "22K (Hallmark / 916)" },
        { v: "18K", label: "18K (750)" },
        { v: "14K", label: "14K (585)" },
      ]
    : [
        { v: "999", label: "999 (Fine)" },
        { v: "925", label: "925 (Sterling)" },
      ];
  const defaultPurity = isGold ? "24K" : "999";
  const activePurity = form.purity || defaultPurity;

  const currentRate = (() => {
    if (!metalRates) return null;
    if (isGold) {
      if (activePurity === "24K") return metalRates.gold_24k_inr_per_gram;
      if (activePurity === "22K") return metalRates.gold_22k_inr_per_gram;
      if (activePurity === "18K") return metalRates.gold_18k_inr_per_gram;
      if (activePurity === "14K") return metalRates.gold_14k_inr_per_gram;
    } else {
      if (activePurity === "999") return metalRates.silver_999_inr_per_gram;
      if (activePurity === "925") return metalRates.silver_925_inr_per_gram;
    }
    return null;
  })();

  const numUnits = parseFloat(form.num_units) || 1;
  const weightPerUnit = parseFloat(form.quantity) || 0;
  const totalGrams = numUnits * weightPerUnit;
  const buyPrice = parseFloat(form.price_per_unit) || 0;
  const diamondValue = form.has_diamond ? (parseFloat(form.diamond_value) || 0) : 0;
  const metalInvested = totalGrams * buyPrice;
  const invested = metalInvested + diamondValue;
  const metalCurrent = form.auto_price && currentRate && totalGrams ? totalGrams * currentRate : 0;
  const estCurrent = form.auto_price ? metalCurrent + diamondValue : (parseFloat(form.current_value) || 0);
  const pnl = estCurrent - invested;
  const pnlPct = invested ? (pnl / invested) * 100 : 0;

  return (
    <div className="space-y-3">
      {/* Live Rate Banner */}
      {metalRates && currentRate ? (
        <div className="bg-amber-500/5 border border-amber-500/15 rounded-md p-2.5 flex items-center justify-between" data-testid="live-rate-banner">
          <div className="flex items-center gap-2">
            <Zap size={12} className="text-amber-400" />
            <span className="text-[11px] text-[var(--text-secondary)]">
              Live {isGold ? "Gold" : "Silver"} {activePurity}: <span className="font-mono text-amber-300 font-semibold">₹{currentRate.toLocaleString("en-IN")}/g</span>
            </span>
          </div>
          <span className="text-[9px] text-[var(--text-muted)]">incl. {metalRates.india_premium_pct || 10}% India premium</span>
        </div>
      ) : (
        <div className="bg-[var(--bg-base)] border border-white/5 rounded-md p-2.5 flex items-center gap-2">
          <Loader2 size={12} className="animate-spin text-[var(--text-muted)]" />
          <span className="text-[11px] text-[var(--text-muted)]">Loading live rate...</span>
        </div>
      )}

      {/* Purity Selector */}
      <div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">Purity</div>
        <div className={`grid ${isGold ? "grid-cols-4" : "grid-cols-2"} gap-1.5`}>
          {purities.map(p => (
            <button
              key={p.v}
              type="button"
              onClick={() => setForm(f => ({ ...f, purity: p.v }))}
              className={`px-2 py-1.5 rounded text-[11px] font-mono transition-colors border ${activePurity === p.v ? "bg-amber-500/15 border-amber-500/40 text-amber-300" : "bg-[var(--bg-base)] border-white/10 text-[var(--text-secondary)] hover:border-white/25"}`}
              data-testid={`purity-${p.v}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Units + Weight + Buy Price */}
      <div className="grid grid-cols-3 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Units / Coins</div>
          <Input type="number" placeholder="1" value={form.num_units} onChange={e => setForm(f => ({ ...f, num_units: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-num-units" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Weight / unit (g) *</div>
          <Input type="number" placeholder="e.g. 1, 8, 10" value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-weight" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Buy Price (₹/g)</div>
          <Input type="number" placeholder="Purchase rate" value={form.price_per_unit} onChange={e => setForm(f => ({ ...f, price_per_unit: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-buy-rate" />
        </div>
      </div>
      {numUnits > 0 && weightPerUnit > 0 && (
        <div className="text-[10px] text-[var(--text-muted)] font-mono -mt-1">
          = <span className="text-amber-300 font-semibold">{totalGrams.toFixed(3)}g</span> total ({numUnits} × {weightPerUnit}g)
        </div>
      )}

      {/* Auto-price toggle */}
      <div className="flex items-center justify-between bg-[var(--bg-base)] border border-white/5 rounded-md px-3 py-2.5">
        <div>
          <div className="text-xs text-white font-medium">Auto-compute current value</div>
          <div className="text-[10px] text-[var(--text-muted)]">Uses live market rate × your weight</div>
        </div>
        <button
          type="button"
          onClick={() => setForm(f => ({ ...f, auto_price: !f.auto_price }))}
          className={`w-9 h-5 rounded-full transition-colors ${form.auto_price ? "bg-amber-500" : "bg-white/10"}`}
          data-testid="auto-price-toggle"
        >
          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.auto_price ? "translate-x-[18px]" : "translate-x-[2px]"}`} />
        </button>
      </div>

      {/* Diamond / Stones addon (Gold only) */}
      {isGold && (
        <div className="bg-[var(--bg-base)] border border-white/5 rounded-md px-3 py-2.5 space-y-2.5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-white font-medium">Has diamond / stones</div>
              <div className="text-[10px] text-[var(--text-muted)]">Added as a flat ₹ amount (no live appreciation)</div>
            </div>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, has_diamond: !f.has_diamond, diamond_value: !f.has_diamond ? f.diamond_value : "" }))}
              className={`w-9 h-5 rounded-full transition-colors ${form.has_diamond ? "bg-cyan-500" : "bg-white/10"}`}
              data-testid="diamond-toggle"
            >
              <div className={`w-4 h-4 rounded-full bg-white transition-transform ${form.has_diamond ? "translate-x-[18px]" : "translate-x-[2px]"}`} />
            </button>
          </div>
          {form.has_diamond && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1">Diamond / Stone Value (₹)</div>
              <Input
                type="number"
                placeholder="e.g. 50000"
                value={form.diamond_value || ""}
                onChange={e => setForm(f => ({ ...f, diamond_value: e.target.value }))}
                className="bg-[var(--bg-base)] border-white/10 text-white"
                data-testid="diamond-value-input"
              />
            </div>
          )}
        </div>
      )}

      {/* Manual current_value if auto is off */}
      {!form.auto_price && (
        <Input type="number" placeholder="Current Value (₹) *" value={form.current_value} onChange={e => setForm(f => ({ ...f, current_value: e.target.value }))} className="bg-[var(--bg-base)] border-white/10 text-white" data-testid="asset-value" />
      )}

      {/* Live preview */}
      {totalGrams > 0 && (
        <div className="bg-[var(--bg-base)] border border-white/5 rounded-md p-3 space-y-1.5">
          <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Total Weight</span><span className="font-mono text-white">{totalGrams.toFixed(3)} g</span></div>
          <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Metal Invested</span><span className="font-mono text-[var(--text-secondary)]">₹{metalInvested.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          {form.has_diamond && diamondValue > 0 && (
            <div className="flex justify-between text-xs"><span className="text-cyan-400">+ Diamond / Stones</span><span className="font-mono text-cyan-300">₹{diamondValue.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          )}
          <div className="flex justify-between text-xs pt-1 border-t border-white/5"><span className="text-[var(--text-muted)]">Total Invested</span><span className="font-mono text-white">₹{invested.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          {form.auto_price && metalCurrent > 0 && (
            <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Metal Current (live)</span><span className="font-mono text-[var(--text-secondary)]">₹{metalCurrent.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          )}
          <div className="flex justify-between text-xs"><span className="text-[var(--text-muted)]">Current {form.auto_price && "(live)"}</span><span className="font-mono text-white font-semibold">₹{estCurrent.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span></div>
          {invested > 0 && estCurrent > 0 && (
            <div className="flex justify-between text-xs pt-1 border-t border-white/5"><span className="text-[var(--text-muted)]">P&L</span><span className={`font-mono font-semibold ${pnl >= 0 ? "text-[var(--positive)]" : "text-[var(--negative)]"}`}>{pnl >= 0 ? "+" : ""}₹{pnl.toLocaleString("en-IN", { maximumFractionDigits: 0 })} ({pnlPct >= 0 ? "+" : ""}{pnlPct.toFixed(1)}%)</span></div>
          )}
        </div>
      )}
    </div>
  );
}

function AssetPhotoCell({ asset, onChanged }) {
  const [uploading, setUploading] = React.useState(false);
  const [lightbox, setLightbox] = React.useState(false);
  const [ver, setVer] = React.useState(() => Date.now());  // cache buster
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so same file can be picked again
    e.target.value = "";
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await uploadAssetPhoto(asset.id, fd);
      toast.success("Photo uploaded");
      setVer(Date.now());
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Remove this photo?")) return;
    try {
      await deleteAssetPhoto(asset.id);
      toast.success("Photo removed");
      setVer(Date.now());
      onChanged?.();
    } catch {
      toast.error("Delete failed");
    }
  };

  const photoSrc = `${assetPhotoUrl(asset.id)}?v=${ver}`;

  return (
    <>
      <div className="flex-shrink-0 relative group" data-testid={`asset-photo-${asset.id}`}>
        {asset.has_photo ? (
          <>
            <button
              type="button"
              onClick={() => setLightbox(true)}
              className="w-12 h-12 rounded-md overflow-hidden border border-white/10 hover:border-[var(--brand)]/40 transition-colors"
            >
              <img
                src={photoSrc}
                alt={asset.name}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="absolute -top-1.5 -right-1.5 bg-[var(--negative)]/90 hover:bg-[var(--negative)] rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
              title="Remove photo"
              data-testid={`delete-photo-${asset.id}`}
            >
              <X size={9} className="text-white" />
            </button>
          </>
        ) : (
          <div className="flex flex-col gap-0.5">
            <div className="flex gap-0.5">
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                title="Take photo"
                className="w-[22px] h-[22px] flex items-center justify-center rounded bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[var(--brand)]/40"
                data-testid={`camera-btn-${asset.id}`}
              >
                {uploading ? <Loader2 size={11} className="animate-spin text-[var(--text-muted)]" /> : <Camera size={11} className="text-[var(--text-muted)]" />}
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Upload photo"
                className="w-[22px] h-[22px] flex items-center justify-center rounded bg-white/5 border border-white/10 hover:bg-white/10 hover:border-[var(--brand)]/40"
                data-testid={`upload-btn-${asset.id}`}
              >
                <Upload size={11} className="text-[var(--text-muted)]" />
              </button>
            </div>
            <div className="text-[8px] text-[var(--text-muted)] text-center">photo</div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
          onChange={onPick}
          className="hidden"
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPick}
          className="hidden"
        />
      </div>

      {lightbox && asset.has_photo && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(false)}
          data-testid={`lightbox-${asset.id}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
          >
            <X size={20} />
          </button>
          <img
            src={photoSrc}
            alt={asset.name}
            className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
